import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtNum, fmtDate, UPE_COLORS, USOS, ESTATUS_UPE } from "../lib/format";
import GridMap from "../components/maps/GridMap";
import UpePanel from "../components/UpePanel";
import UpeFormModal from "../components/UpeFormModal";
import { Card, ProgressBar } from "../components/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Map as MapIcon, Table2, Plus, FileText, Upload, Eye, Sparkles, Loader2 } from "lucide-react";

export default function MapaMaestro() {
  const { pid } = useParams();
  const { canEdit } = useAuth();
  const [prop, setProp] = useState(null);
  const [upes, setUpes] = useState(null);
  const [selected, setSelected] = useState(null);
  const [vista, setVista] = useState("mapa");
  const [filtroUso, setFiltroUso] = useState("");
  const [filtroEstatus, setFiltroEstatus] = useState("");
  const [upeModal, setUpeModal] = useState({ open: false, upe: null });
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [propuestas, setPropuestas] = useState(null); // resultado IA
  const fileRef = useRef(null);

  const load = () => {
    api.get(`/properties/${pid}`).then((r) => setProp(r.data));
    api.get(`/properties/${pid}/upes`).then((r) => setUpes(r.data));
  };
  useEffect(() => { setProp(null); setUpes(null); setSelected(null); load(); }, [pid]); // eslint-disable-line

  const subirMasterplan = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") return toast.error("El Master Plan debe ser un PDF");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/properties/${pid}/masterplan`, fd);
      toast.success("Master Plan cargado");
      load();
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
    e.target.value = "";
  };

  const verMasterplan = async () => {
    try {
      const r = await api.get(`/properties/${pid}/masterplan`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const extraer = async () => {
    setExtracting(true);
    try {
      const r = await api.post(`/properties/${pid}/masterplan/extraer-upes`, {}, { timeout: 240000 });
      const found = (r.data.upes || []).map((u) => ({ ...u, incluir: true }));
      if (found.length === 0) {
        toast.info("La IA no encontró unidades identificables en el PDF. Puedes agregarlas manualmente.");
      } else {
        setPropuestas(found);
      }
    } catch (e) { toast.error(apiError(e)); }
    setExtracting(false);
  };

  const confirmarPropuestas = async () => {
    const sel = propuestas.filter((u) => u.incluir);
    if (sel.length === 0) return toast.error("Selecciona al menos una unidad");
    try {
      await api.post(`/properties/${pid}/upes/bulk`, { upes: sel.map(({ incluir, ...u }) => u) });
      toast.success(`${sel.length} UPEs creadas desde el Master Plan`);
      setPropuestas(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setProp2 = (i, k, v) => {
    setPropuestas((prev) => prev.map((u, idx) => (idx === i ? { ...u, [k]: v } : u)));
  };

  const mp = prop?.master_plan;

  return (
    <div className="flex h-full">
      <div className="flex-1 p-4 sm:p-8 overflow-y-auto scrollbar-thin">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between flex-wrap gap-4 mb-5">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Mapa Maestro</p>
            <h1 className="font-display text-3xl font-bold text-[#002B49]">UPEs del proyecto</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={filtroUso || "todos"} onValueChange={(v) => setFiltroUso(v === "todos" ? "" : v)}>
              <SelectTrigger data-testid="filtro-uso" className="w-40 h-9 bg-white"><SelectValue placeholder="Uso" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los usos</SelectItem>
                {USOS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filtroEstatus || "todos"} onValueChange={(v) => setFiltroEstatus(v === "todos" ? "" : v)}>
              <SelectTrigger data-testid="filtro-estatus" className="w-48 h-9 bg-white"><SelectValue placeholder="Estatus" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los estatus</SelectItem>
                {ESTATUS_UPE.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="toggle-vista-button" variant="outline" onClick={() => setVista(vista === "mapa" ? "tabla" : "mapa")}
              className="h-9 gap-2 border-[#002B49] text-[#002B49]">
              {vista === "mapa" ? <><Table2 size={14} /> Tabla</> : <><MapIcon size={14} /> Mapa</>}
            </Button>
            {canEdit && (
              <Button data-testid="agregar-upe-button" onClick={() => setUpeModal({ open: true, upe: null })}
                className="h-9 gap-2 bg-[#00A3B5] hover:bg-[#008C9C] text-white">
                <Plus size={14} /> Agregar UPE
              </Button>
            )}
          </div>
        </motion.div>

        {/* Master Plan */}
        <Card className="p-4 mb-5" testId="masterplan-card">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-md bg-[#002B49]/5 flex items-center justify-center flex-shrink-0">
              <FileText size={18} className="text-[#002B49]" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <p className="font-display font-bold text-[#002B49] text-sm">Master Plan del proyecto</p>
              {mp ? (
                <p className="text-xs text-slate-500 truncate">{mp.nombre_original} · subido por {mp.subido_por} · {fmtDate(mp.fecha)}</p>
              ) : (
                <p className="text-xs text-slate-400">Sube el PDF del master plan para consultarlo y extraer sus UPEs con IA.</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {mp && (
                <Button data-testid="ver-masterplan-button" variant="outline" onClick={verMasterplan} className="h-8 gap-1.5 text-xs">
                  <Eye size={13} /> Ver PDF
                </Button>
              )}
              {canEdit && (
                <>
                  <Button data-testid="subir-masterplan-button" variant="outline" disabled={uploading}
                    onClick={() => fileRef.current?.click()} className="h-8 gap-1.5 text-xs">
                    <Upload size={13} /> {uploading ? "Subiendo..." : mp ? "Reemplazar PDF" : "Subir PDF"}
                  </Button>
                  <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="masterplan-file-input" onChange={subirMasterplan} />
                  {mp && (
                    <Button data-testid="extraer-ia-button" onClick={extraer} disabled={extracting}
                      className="h-8 gap-1.5 text-xs bg-[#002B49] hover:bg-[#001D33] text-white">
                      {extracting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                      {extracting ? "Analizando con IA..." : "Extraer UPEs con IA"}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          {extracting && (
            <p className="text-xs text-[#00A3B5] mt-2 animate-pulse">La IA (Gemini) está leyendo el Master Plan. Esto puede tardar hasta un minuto...</p>
          )}
        </Card>

        {!upes ? <p className="text-slate-400 animate-pulse">Cargando mapa...</p> : vista === "mapa" ? (
          <Card className="p-6">
            <GridMap upes={upes} onSelect={(u) => setSelected(u.id)} selectedId={selected}
              filtroUso={filtroUso} filtroEstatus={filtroEstatus} />
            <div className="flex flex-wrap gap-4 text-xs text-slate-500 mt-4 pt-4 border-t border-slate-100">
              {Object.entries(UPE_COLORS).map(([e, c]) => (
                <span key={e} className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm border border-slate-300" style={{ background: c }} />{e}
                </span>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="overflow-hidden" testId="tabla-upes">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Clave</th><th className="px-4 py-3">Lote</th>
                  <th className="px-4 py-3">Uso</th><th className="px-4 py-3 text-right">Superficie (m²)</th>
                  <th className="px-4 py-3 text-right">Llaves</th><th className="px-4 py-3">Estatus</th>
                  <th className="px-4 py-3 w-36">Avance</th>
                </tr>
              </thead>
              <tbody>
                {upes.filter((u) => (!filtroUso || u.uso === filtroUso) && (!filtroEstatus || u.estatus === filtroEstatus)).map((u) => (
                  <tr key={u.id} data-testid={`upe-row-${u.clave}`} onClick={() => setSelected(u.id)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-bold text-[#002B49]">{u.clave}</td>
                    <td className="px-4 py-3 text-slate-600">{u.lote}</td>
                    <td className="px-4 py-3 text-slate-600">{u.uso}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{fmtNum(u.superficie_m2)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{fmtNum(u.llaves_total)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: UPE_COLORS[u.estatus] }} />{u.estatus}
                      </span>
                    </td>
                    <td className="px-4 py-3"><ProgressBar value={u.avance} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>
        )}
        <p className="text-xs text-slate-400 mt-4">
          Los trámites del macroproyecto aplican al lote maestro completo.
          Consulta <Link to={`/p/${pid}/tramites`} className="text-[#00A3B5] hover:underline">Tramitología y Permisología</Link>.
        </p>
      </div>
      {selected && <UpePanel upeId={selected} onClose={() => setSelected(null)} onChanged={load}
        onEdit={(u) => setUpeModal({ open: true, upe: u })} />}

      <UpeFormModal open={upeModal.open} upe={upeModal.upe} pid={pid}
        onClose={() => setUpeModal({ open: false, upe: null })} onSaved={load} />

      {/* Preview de UPEs extraídas por IA */}
      <Dialog open={!!propuestas} onOpenChange={(v) => !v && setPropuestas(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="propuestas-ia-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-[#002B49] flex items-center gap-2">
              <Sparkles size={17} className="text-[#00A3B5]" /> UPEs detectadas en el Master Plan
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Revisa y ajusta lo que detectó la IA antes de crear las unidades. Podrás editarlas siempre.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[640px]">
              <thead>
                <tr className="text-left text-slate-500 uppercase tracking-wider">
                  <th className="px-2 py-2"></th><th className="px-2 py-2">Clave</th><th className="px-2 py-2">Uso</th>
                  <th className="px-2 py-2">m²</th><th className="px-2 py-2">Hotel</th><th className="px-2 py-2">Condo</th>
                  <th className="px-2 py-2">Descripción</th>
                </tr>
              </thead>
              <tbody>
                {(propuestas || []).map((u, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${!u.incluir ? "opacity-40" : ""}`} data-testid={`propuesta-row-${i}`}>
                    <td className="px-2 py-2">
                      <Checkbox checked={u.incluir} onCheckedChange={(v) => setProp2(i, "incluir", !!v)} data-testid={`propuesta-check-${i}`} />
                    </td>
                    <td className="px-2 py-2"><Input className="h-7 text-xs w-24" value={u.clave} onChange={(e) => setProp2(i, "clave", e.target.value)} /></td>
                    <td className="px-2 py-2">
                      <Select value={USOS.includes(u.uso) ? u.uso : "Otro"} onValueChange={(v) => setProp2(i, "uso", v)}>
                        <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{USOS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-2"><Input className="h-7 text-xs w-20" type="number" value={u.superficie_m2} onChange={(e) => setProp2(i, "superficie_m2", Number(e.target.value))} /></td>
                    <td className="px-2 py-2"><Input className="h-7 text-xs w-16" type="number" value={u.llaves_hotel} onChange={(e) => setProp2(i, "llaves_hotel", Number(e.target.value))} /></td>
                    <td className="px-2 py-2"><Input className="h-7 text-xs w-16" type="number" value={u.llaves_condo} onChange={(e) => setProp2(i, "llaves_condo", Number(e.target.value))} /></td>
                    <td className="px-2 py-2 max-w-[200px]"><p className="truncate text-slate-500" title={u.descripcion}>{u.descripcion}</p></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPropuestas(null)}>Cancelar</Button>
            <Button data-testid="crear-upes-ia-button" onClick={confirmarPropuestas} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">
              Crear {propuestas?.filter((u) => u.incluir).length || 0} UPEs
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
